//
// Copyright 2025 DXOS.org
//

import { type EditorView } from '@codemirror/view';
import React, {
  Fragment,
  type KeyboardEvent,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { addEventListener } from '@dxos/async';
import { invariant } from '@dxos/invariant';
import { useControllableState } from '@dxos/react-hooks';
import * as Field from '@dxos/react-ui/Field';
import * as Hooks from '@dxos/react-ui/Hooks';
import * as Icon from '@dxos/react-ui/Icon';
import * as Input from '@dxos/react-ui/Input';
import * as Layout from '@dxos/react-ui/Layout';
import * as Popover from '@dxos/react-ui/Popover';
import * as Theme from '@dxos/react-ui/Theme';
import * as Typography from '@dxos/react-ui/Typography';
import * as VirtualAnchor from '@dxos/react-ui/VirtualAnchor';
import { DX_ANCHOR_ACTIVATE, type DxAnchorActivate } from '@dxos/ui-types';

import { translationKey } from '#translations';

import { type EditorMenuGroup, type EditorMenuItem, getMenuItem } from './menu.ts';

export type EditorMenuProviderProps = PropsWithChildren<{
  // Provided as a getter (not a value prop) so the live `EditorView` is never carried in a React prop that
  // the dev render-logger would walk into a cross-origin frame. See `controller.ts` for the full rationale.
  getView?: () => EditorView | null;
  groups?: EditorMenuGroup[];
  currentItem?: string;
  open?: boolean;
  defaultOpen?: boolean;
  numItems?: number;
  /** Render the menu as a combobox: the popover owns the query input and takes focus from the editor. */
  search?: boolean;
  query?: string;
  searchPlaceholder?: string;
  onOpenChange?: (event: { view: EditorView; open: boolean; trigger?: string }) => void;
  onActivate?: (event: { view: EditorView; trigger?: string }) => void;
  onSelect?: (event: { view: EditorView; item: EditorMenuItem }) => void;
  onCancel?: (event: { view: EditorView }) => void;
  onQueryChange?: (query: string) => void;
  onNavigate?: (direction: 'up' | 'down') => void;
}>;

/**
 * Implements the Popover and listens for the `dx-anchor-activate` event from the `popover` extension's decoration.
 * NOTE: We don't use Menu because the command menu needs to manage focus explicitly.
 * I.e., focus must remain in the editor while displaying the menu (for type-ahead).
 */
export const EditorMenuProvider = ({
  children,
  getView,
  groups,
  currentItem,
  open: openProp,
  defaultOpen,
  numItems = 8,
  search,
  query = '',
  searchPlaceholder,
  onOpenChange,
  onActivate,
  onSelect,
  onCancel,
  onQueryChange,
  onNavigate,
}: EditorMenuProviderProps) => {
  const { t } = Hooks.useTranslation(translationKey);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  // Hold the latest `getView` so callbacks/effects always read the current view without re-subscribing.
  const getViewRef = Hooks.useDynamicRef(getView);
  const [open, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: (open) => {
      const view = getViewRef.current?.();
      invariant(view);
      onOpenChange?.({ view, open });
    },
  });

  const [root, setRoot] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!root) {
      return;
    }

    // Listen for trigger.
    return addEventListener(
      root,
      DX_ANCHOR_ACTIVATE as any,
      (event: DxAnchorActivate) => {
        const { trigger, eid } = event;
        if (!eid) {
          triggerRef.current = trigger as HTMLButtonElement;
          if (onActivate) {
            const view = getViewRef.current?.();
            if (view) {
              onActivate({ view, trigger: trigger.getAttribute('data-trigger') ?? undefined });
            }
          } else {
            requestAnimationFrame(() => setOpen(true));
          }
        }
      },
      {
        capture: true,
        passive: false,
      },
    );
  }, [root, onActivate]);

  const handleSelect = useCallback<NonNullable<MenuProps['onSelect']>>(
    (item) => {
      const view = getViewRef.current?.();
      invariant(view);
      onSelect?.({ view, item });
      // Close the popover after acting (e.g. Delete) and return focus to the editor; otherwise the menu
      // lingers open over the now-changed document.
      setOpen(false);
      view.focus();
    },
    [getViewRef, onSelect, setOpen],
  );

  const menuGroups = groups?.filter((group) => group.items.length > 0) ?? [];

  // Handing off from the slash menu refocuses the editor and reopens the popover a frame later, so
  // neither `autoFocus` nor `onOpenAutoFocus` lands on the input. Claim focus a frame after the
  // modal-state dispatch `handleOpenChange` schedules, which otherwise hands focus back to CodeMirror.
  const searchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!search || !open) {
      return;
    }

    let inner: number;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => searchInputRef.current?.focus());
    });

    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [search, open]);

  const handleSearchKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      switch (event.key) {
        case 'ArrowUp':
        case 'ArrowDown': {
          event.preventDefault();
          onNavigate?.(event.key === 'ArrowUp' ? 'up' : 'down');
          break;
        }

        case 'Enter': {
          const item = getMenuItem(menuGroups, currentItem);
          if (item) {
            event.preventDefault();
            handleSelect(item);
          }
          break;
        }
      }
    },
    [menuGroups, currentItem, handleSelect, onNavigate],
  );

  return (
    <Popover.Root
      open={open}
      onOpenChange={({ open }) => setOpen(open)}
      positioning={{ ...VirtualAnchor.virtualAnchor(triggerRef), placement: 'bottom-start' }}
      // In search mode the query is typed into the popover's own input, so it must take focus.
      autoFocus={!!search}
      // Focus stays in the editor; the menu machine still routes Escape here.
      onEscapeKeyDown={() => {
        const currentView = getViewRef.current?.();
        if (currentView) {
          onCancel?.({ view: currentView });
        }
      }}
    >
      {/* Menu. */}
      <Popover.Content
        classNames={!search && !menuGroups.length ? 'hidden' : undefined}
        style={{
          // The search input shares the box, so `numItems` keeps meaning "items visible".
          maxBlockSize: 36 * numItems + 10 + (search ? 36 : 0),
        }}
      >
        {search && (
          <Field.Root>
            <Input.Root
              ref={searchInputRef}
              variant='subdued'
              classNames='shrink-0 mb-1'
              value={query}
              placeholder={searchPlaceholder}
              // Placeholder text is not a persistent accessible name, and it is optional — fall
              // back to the generic label so the input is never anonymous.
              aria-label={searchPlaceholder ?? t('search.label')}
              onChange={(event) => onQueryChange?.(event.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
          </Field.Root>
        )}
        <Popover.Body>
          <Menu groups={menuGroups} currentItem={currentItem} onSelect={handleSelect} />
        </Popover.Body>
      </Popover.Content>

      {/* Content */}
      <div className='contents' ref={setRoot}>
        {children}
      </div>
    </Popover.Root>
  );
};

//
// Menu
//

type MenuProps = {
  groups: EditorMenuGroup[];
} & Pick<MenuGroupProps, 'currentItem' | 'onSelect'>;

const Menu = ({ groups, currentItem, onSelect }: MenuProps) => {
  return (
    <ul>
      {groups.map((group, index) => (
        <Fragment key={group.id}>
          <MenuGroup group={group} currentItem={currentItem} onSelect={onSelect} />
          {index < groups.length - 1 && <Layout.Separator />}
        </Fragment>
      ))}
    </ul>
  );
};

//
// Menu Group
//

type MenuGroupProps = {
  group: EditorMenuGroup;
  currentItem?: string;
} & Pick<MenuItemProps, 'onSelect'>;

const MenuGroup = ({ group, currentItem, onSelect }: MenuGroupProps) => {
  const { t } = Hooks.useTranslation();

  return (
    <>
      {group.label && (
        <Typography.Text tone='muted' classNames='px-2'>
          {Theme.toLocalizedString(group.label, t)}
        </Typography.Text>
      )}

      {group.items.map((item) => (
        <MenuItem key={item.id} item={item} current={currentItem === item.id} onSelect={onSelect} />
      ))}
    </>
  );
};

//
// Menu Item
//

type MenuItemProps = {
  item: EditorMenuItem;
  current: boolean;
  onSelect?: (item: EditorMenuItem) => void;
};

const MenuItem = ({ item, current, onSelect }: MenuItemProps) => {
  const { t } = Hooks.useTranslation();

  const listRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (current && listRef.current) {
      // Instant, not smooth: held arrow keys retarget faster than a smooth scroll can settle, and each
      // new animation supersedes the last, so the list stalls then jumps.
      listRef.current.scrollIntoView({ behavior: 'instant', block: 'nearest' });
    }
  }, [current]);

  const handleSelect = useCallback(() => onSelect?.(item), [item, onSelect]);

  return (
    // Menu row metrics without a Menu machine: the popover keeps focus in the editor, so `current` is the highlight.
    <li ref={listRef} className='dx-menu-item' data-highlighted={current ? '' : undefined} onClick={handleSelect}>
      {item.icon && <Icon.Icon icon={item.icon} hue={item.iconHue} />}
      <span className='dx-menu-item-text'>{Theme.toLocalizedString(item.label, t)}</span>
    </li>
  );
};
